import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import {
  Activity,
  User,
  ArrowRight,
  ArrowLeft,
  Loader2,
  MailCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import apiClient from '@/api/client';
import { AUTH_BASE_URL } from '@/constants';

const forgotSchema = z.object({
  identifier: z
    .string()
    .min(1, 'Username or Email is required')
    .regex(/^[a-zA-Z0-9@._]+$/, 'Please enter a valid alphanumeric username or registered email address'),
});

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm({
    resolver: zodResolver(forgotSchema),
    defaultValues: { identifier: '' },
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await apiClient.post(`${AUTH_BASE_URL}/forgot-password`, { identifier: data.identifier });
      setSubmitted(true);
      toast.success('Password recovery instructions sent to registered email.');
    } catch (err) {
      // In security flows, still show success state to prevent username enumeration
      setSubmitted(true);
      toast.info('If your account exists, password recovery instructions have been dispatched.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md space-y-6">
        <div className="flex items-center gap-3 justify-center mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center shadow-md">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading font-bold text-2xl text-slate-900">PulseFlow PMS</span>
        </div>

        <Card className="border border-slate-200/80 shadow-xl bg-white rounded-2xl">
          <CardHeader className="space-y-1 pb-4 text-center">
            <CardTitle className="text-xl font-heading font-bold text-slate-900">
              {submitted ? 'Check Your Email' : 'Reset Password'}
            </CardTitle>
            <CardDescription className="text-xs text-slate-500">
              {submitted
                ? 'We have dispatched password recovery instructions to the mandatory email address associated with your account.'
                : 'Enter your alphanumeric username or registered email address to receive a secure password reset link.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-5">
            {submitted ? (
              <div className="text-center space-y-4 py-4">
                <div className="w-12 h-12 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center mx-auto">
                  <MailCheck className="w-6 h-6" />
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Please check your inbox (and spam folder) for the password reset token link.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white text-xs h-10 font-bold"
                >
                  Return to Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="identifier" className="text-xs font-semibold text-slate-700">
                    Alphanumeric Username or Registered Email
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="identifier"
                      type="text"
                      placeholder="e.g. drrajesh or dr.rajesh@apollohospital.in"
                      className="pl-10 text-xs h-10 border-slate-200 focus-visible:ring-teal-500"
                      {...form.register('identifier')}
                    />
                  </div>
                  {form.formState.errors.identifier && (
                    <p className="text-xs text-red-500">{form.formState.errors.identifier.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold h-10 text-xs shadow-md"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending Recovery Link...
                    </>
                  ) : (
                    <>
                      Send Reset Instructions
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <div className="pt-2 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-xs font-semibold text-teal-700 hover:underline"
                  >
                    <ArrowLeft className="mr-1 h-3.5 w-3.5" />
                    Back to Login
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;

